import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { AlertCircle, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

// ============================================================================
// Types
// ============================================================================

interface Proposal {
  id: string;
  site_id: string;
  site_name: string | null;
  sector_id: number;
  parent_category_path: string;
  status: string;
  verdict: string;
  reason: string | null;
  evidence: Record<string, unknown>;
  before_metrics: Record<string, unknown>;
  hitl_decision: string | null;
  hitl_reviewer_notes: string | null;
  hitl_decided_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Child {
  id: string;
  child_category_path: string;
  display_name: string;
  product_type_keys: string[];
  substitutability_group: string | null;
  expected_product_count: number | null;
}

interface SampleProduct {
  product_id: string;
  original_category_path: string;
  override_category_path: string;
  assignment_confidence: string;
  override_status: string;
  title: string | null;
  brand: string | null;
  price: string | null;
  image_url: string | null;
}

interface DetailData {
  proposal: Proposal;
  children: Child[];
  sample_products: SampleProduct[];
}

// ============================================================================
// Helpers
// ============================================================================

const STATUS_META: Record<string, { label: string; cls: string }> = {
  needs_hitl:         { label: 'Needs HITL',         cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  proposed:           { label: 'Proposed',            cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  applied:            { label: 'Applied',             cls: 'bg-green-100 text-green-800 border-green-300' },
  rolled_back:        { label: 'Rejected',            cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  failed:             { label: 'Failed',              cls: 'bg-red-100 text-red-800 border-red-300' },
  dry_run:            { label: 'Dry run',             cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  production_ready:   { label: 'Production ready',   cls: 'bg-green-100 text-green-800 border-green-300' },
};

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function pct(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? '—' : `${(n * 100).toFixed(0)}%`;
}

function fmtDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleString() : '—';
}

// ============================================================================
// Page
// ============================================================================

export default function CategoryGranularityHITLDetail() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Decision dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<'approve_split' | 'reject_split' | null>(null);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/category-granularity/hitl-proposals/${proposalId}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [proposalId]);

  function openDialog(decision: 'approve_split' | 'reject_split') {
    setPendingDecision(decision);
    setReviewerNotes('');
    setSubmitError(null);
    setSubmitResult(null);
    setDialogOpen(true);
  }

  async function submitDecision() {
    if (!data || !pendingDecision) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await authFetch(
        `/api/category-granularity/${data.proposal.site_id}/hitl-approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proposalId: data.proposal.id,
            decision: pendingDecision,
            reviewer_notes: reviewerNotes || undefined,
          }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 202) {
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const label = pendingDecision === 'approve_split' ? 'Approved' : 'Rejected';
      setSubmitResult(
        res.status === 202
          ? `${label} (approval committed; split apply will retry separately)`
          : `${label} successfully`,
      );
      setDialogOpen(false);
      await load();
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const proposal = data?.proposal;
  const isActionable = proposal?.status === 'needs_hitl';

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/catalog/category-splits')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to list
        </Button>
        <h1 className="text-xl font-bold">Category Split Review</h1>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {submitResult && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{submitResult}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      ) : !proposal ? null : (
        <>
          {/* Proposal summary */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">
                  {proposal.parent_category_path}
                </CardTitle>
                <StatusBadge status={proposal.status} />
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground font-medium mb-1">Site</p>
                <p>{proposal.site_name ?? proposal.site_id}</p>
              </div>
              <div>
                <p className="text-muted-foreground font-medium mb-1">Verdict</p>
                <p>{proposal.verdict}</p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground font-medium mb-1">Rationale</p>
                <p className="text-muted-foreground">{proposal.reason ?? '—'}</p>
              </div>
              {proposal.hitl_decision && (
                <>
                  <div>
                    <p className="text-muted-foreground font-medium mb-1">Decision</p>
                    <p>{proposal.hitl_decision} — {fmtDate(proposal.hitl_decided_at)}</p>
                  </div>
                  {proposal.hitl_reviewer_notes && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground font-medium mb-1">Reviewer notes</p>
                      <p>{proposal.hitl_reviewer_notes}</p>
                    </div>
                  )}
                </>
              )}
              <div>
                <p className="text-muted-foreground font-medium mb-1">Created</p>
                <p>{fmtDate(proposal.created_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground font-medium mb-1">Proposal ID</p>
                <p className="font-mono text-xs">{proposal.id}</p>
              </div>
            </CardContent>
          </Card>

          {/* Evidence signals — key for HITL reviewers to understand why human review was triggered.
              NEEDS_HITL proposals have no children yet (materialized at approve time), so signals
              are the primary review surface. */}
          {proposal.evidence && Object.keys(proposal.evidence).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Evidence signals</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-slate-50 border rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap">
                  {JSON.stringify(proposal.evidence, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Notice when no children yet — expected for needs_hitl proposals */}
          {data.children.length === 0 && proposal.status === 'needs_hitl' && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Sub-categories will be derived from the signals above when you approve this split.
                Reject if the signals do not justify splitting.
              </AlertDescription>
            </Alert>
          )}

          {/* Proposed sub-categories — only present for approved needs_hitl or
              split_recommended/split_required proposals (not while still in needs_hitl). */}
          {data.children.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Proposed sub-categories ({data.children.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sub-category</TableHead>
                      <TableHead>Display name</TableHead>
                      <TableHead>Expected products</TableHead>
                      <TableHead>Product types</TableHead>
                      <TableHead>Group</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.children.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono text-xs">{c.child_category_path}</TableCell>
                        <TableCell className="font-medium">{c.display_name}</TableCell>
                        <TableCell>{c.expected_product_count ?? '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {c.product_type_keys.join(', ') || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {c.substitutability_group ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Sample products */}
          {data.sample_products.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Sample products ({data.sample_products.length} shown, up to 20)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Proposed sub-category</TableHead>
                      <TableHead>Confidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.sample_products.map((p) => (
                      <TableRow key={p.product_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {p.image_url && (
                              <img src={p.image_url} alt="" className="h-10 w-10 object-cover rounded border" />
                            )}
                            <div>
                              <p className="font-medium text-sm line-clamp-1">{p.title ?? p.product_id}</p>
                              {p.price && <p className="text-xs text-muted-foreground">{p.price}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{p.brand ?? '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{p.override_category_path}</TableCell>
                        <TableCell className="text-sm">{pct(p.assignment_confidence)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Action buttons */}
          {isActionable && (
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => openDialog('reject_split')}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject split
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => openDialog('approve_split')}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve split
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirm dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingDecision === 'approve_split' ? 'Approve split' : 'Reject split'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pendingDecision === 'approve_split'
              ? 'Products will be reclassified into the proposed sub-categories. This action cannot be undone without a rollback.'
              : 'The proposal will be marked as rejected. No products will move.'}
          </p>
          <Textarea
            placeholder="Reviewer notes (optional)"
            value={reviewerNotes}
            onChange={(e) => setReviewerNotes(e.target.value)}
            rows={3}
          />
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={submitDecision}
              disabled={submitting}
              className={
                pendingDecision === 'approve_split'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {submitting ? 'Submitting…' : pendingDecision === 'approve_split' ? 'Confirm approval' : 'Confirm rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
